"use client";

import { RadioGroupField } from "@/components/form/fields/RadioGroupField/RadioGroupField";
import { EnergyType } from "@/types/data";
import { track } from "@/utilities/analytics";
import { siteAPIURL } from "@/utilities/constants";
import { ErrorMessage } from "@hookform/error-message";
import clsx from "clsx";
import { motion } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InputField } from "../../form/fields/InputField/InputField";
import { CheckboxInput } from "../../form/input/CheckboxInput/CheckboxInput";
import { AddressSearchBox } from "../AddressSearchBox/AddressSearchBox";
import { Button } from "../Button/Button";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";
import { NmiSearchModal } from "../NmiSearchModal/NmiSearchModal";
import styles from "./ProductLookup.module.scss";
import type { AddressSearchSupportContent, NmiSearchModalContent } from "@/sanity/queries";

interface ProductLookupProps {
	className?: string;
	searchParams?: Record<string, string>;
	addressSearchSupportContent?: AddressSearchSupportContent | null;
	nmiSearchModalContent?: NmiSearchModalContent | null;
}

type ProductLookupFormValues = {
	address: string;
	promo: string;
	energy: string;
	hasPromoCode: boolean;
	addressKey: string;
	nmi: string;
};

type AddressSuggestion = {
	text: string;
	globalAddressKey: string;
};

export const ProductLookup = ({
	className,
	searchParams,
	addressSearchSupportContent,
	nmiSearchModalContent,
}: ProductLookupProps) => {
	const router = useRouter();
	const pathname = usePathname();
	const initialNmi = searchParams?.nmi || "";
	const initialAddress = initialNmi ? getNmiDisplayValue(initialNmi) : searchParams?.address || "";

	const formMethods = useForm<ProductLookupFormValues>({
		defaultValues: {
			address: initialAddress,
			promo: searchParams?.promo || "",
			energy: initialNmi ? "electricity" : searchParams?.energy || "",
			hasPromoCode: searchParams?.promo ? true : false,
			addressKey: searchParams?.addressKey || "",
			nmi: initialNmi,
		},
		reValidateMode: "onChange",
		shouldFocusError: false,
	});

	const [address, setAddress] = useState(initialAddress);
	const [nmi, setNmi] = useState(initialNmi);
	const [isNmiModalOpen, setIsNmiModalOpen] = useState(false);

	const {
		handleSubmit,
		formState: { errors },
		register,
		resetField,
		watch,
		setValue,
		setError,
		clearErrors,
	} = formMethods;

	const addressKey = watch("addressKey");
	const energy = watch("energy");
	const promoCode = watch("promo");
	const isPromoChecked = watch("hasPromoCode");
	const watchedNmi = watch("nmi");

	const [formProcessing, setFormProcessing] = useState(false);

	useEffect(() => {
		if (!searchParams?.nmi || searchParams.energy !== "gas") {
			return;
		}

		const queryParams = new URLSearchParams();
		queryParams.append("nmi", searchParams.nmi);
		queryParams.append("energy", "electricity");

		if (searchParams.promo) {
			queryParams.append("promo", searchParams.promo);
		}

		if (searchParams.requestID) {
			queryParams.append("requestID", searchParams.requestID);
		}

		router.replace(`/apply-online?${queryParams.toString()}#product-result`);
	}, [router, searchParams]);

	useEffect(() => {
		const nextNmi = searchParams?.nmi || "";
		const nextAddress = nextNmi ? getNmiDisplayValue(nextNmi) : searchParams?.address || "";
		const nextEnergy = nextNmi ? "electricity" : searchParams?.energy || "";

		setNmi(nextNmi);
		setAddress(nextAddress);
		setValue("nmi", nextNmi);
		setValue("address", nextAddress);
		setValue("addressKey", nextNmi ? "" : searchParams?.addressKey || "");
		setValue("energy", nextEnergy);

		if (nextNmi) {
			clearErrors("address");
		}
	}, [clearErrors, searchParams, setValue]);

	useEffect(() => {
		if (watchedNmi && energy === "gas") {
			setNmi("");
			setAddress("");
			setValue("nmi", "");
			setValue("address", "");
			setValue("addressKey", "");
			clearErrors("address");
		}
	}, [clearErrors, energy, setValue, watchedNmi]);

	useEffect(() => {
		if (energy === "gas" && isNmiModalOpen) {
			setIsNmiModalOpen(false);
		}
	}, [energy, isNmiModalOpen]);

	function handleNmiSearch(nmi: string) {
		track({ event: "address_search", search_type: "nmi", energy: "electricity" });

		const nmiDisplayValue = getNmiDisplayValue(nmi);

		setNmi(nmi);
		setAddress(nmiDisplayValue);
		setValue("nmi", nmi);
		setValue("address", nmiDisplayValue);
		setValue("addressKey", "");
		setValue("energy", "electricity");
		clearErrors("address");

		const queryParams = new URLSearchParams();

		queryParams.append("nmi", nmi);
		queryParams.append("energy", "electricity");

		if (promoCode && isPromoChecked) {
			queryParams.append("promo", promoCode);
		}

		queryParams.append("requestID", crypto.randomUUID());

		if (pathname === "/apply-online") {
			router.push(`?${queryParams.toString()}#product-result`);
		} else {
			router.push(`/apply-online?${queryParams.toString()}#product-result`);
		}
	}

	async function resolveAddressSuggestion(address: string) {
		const url = new URL(siteAPIURL + "/signup/address-search");
		url.searchParams.append("address", address);

		const response = await fetch(url);
		const data = await response.json();

		if (!response.ok) {
			throw new Error(data?.error || "Address search failed");
		}

		const suggestions = (data?.suggestions || []) as AddressSuggestion[];
		return suggestions[0] || null;
	}

	const onSubmit: Parameters<typeof handleSubmit>[0] = async data => {
		setFormProcessing(true);

		if (data.nmi && data.energy === "electricity") {
			track({ event: "address_search", search_type: "nmi", energy: "electricity" });

			const queryParams = new URLSearchParams();

			queryParams.append("nmi", data.nmi);
			queryParams.append("energy", "electricity");

			if (promoCode && isPromoChecked) {
				queryParams.append("promo", promoCode);
			}

			queryParams.append("requestID", crypto.randomUUID());

			if (pathname === "/apply-online") {
				router.push(`?${queryParams.toString()}#product-result`);
			} else {
				router.push(`/apply-online?${queryParams.toString()}#product-result`);
			}

			setFormProcessing(false);
			return;
		}

		const typedAddress = (address || data.address || "").trim();
		let selectedAddress = typedAddress;
		let selectedAddressKey = data.addressKey || addressKey;

		if (!selectedAddress) {
			setError("address", { message: "Please enter your connection address" });
			setFormProcessing(false);
			return;
		}

		if (!selectedAddressKey) {
			try {
				const suggestion = await resolveAddressSuggestion(selectedAddress);

				if (!suggestion) {
					track({
						event: "address_search_failed",
						reason: "not_resolved",
						energy: energy as EnergyType,
					});
					setError("address", { message: "Please select a valid address to continue" });
					setFormProcessing(false);
					return;
				}

				selectedAddress = suggestion.text;
				selectedAddressKey = suggestion.globalAddressKey;
				setAddress(selectedAddress);
				setValue("address", selectedAddress);
				setValue("addressKey", selectedAddressKey);
				clearErrors("address");
			} catch (error) {
				console.error("Failed to search address", error);
				track({
					event: "address_search_failed",
					reason: "search_error",
					energy: energy as EnergyType,
				});
				setError("address", {
					message: "We couldn't search that address. Please try again.",
				});
				setFormProcessing(false);
				return;
			}
		}

		track({ event: "address_search", search_type: "address", energy: energy as EnergyType });

		const queryParams = new URLSearchParams();

		queryParams.append("address", selectedAddress);
		queryParams.append("addressKey", selectedAddressKey);
		queryParams.append("energy", energy);

		if (promoCode && isPromoChecked) {
			queryParams.append("promo", promoCode);
		}

		// this is needed in order to refresh the results when the user clicks the button again
		queryParams.append("requestID", crypto.randomUUID());

		if (pathname === "/apply-online") {
			router.push(`?${queryParams.toString()}#product-result`);
		} else {
			router.push(`/apply-online?${queryParams.toString()}#product-result`);
		}

		const productContainer = document.getElementById("product-result");
		if (productContainer) {
			productContainer.scrollIntoView({ behavior: "smooth" });
			setFormProcessing(false);
		}
	};

	return (
		<div className={styles["product-lookup-wrapper"]}>
			<FormProvider {...formMethods}>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className={clsx(styles["product-lookup"], className)}
				>
					<AddressSearchBox
						defaultAddress={address}
						nmi={nmi}
						energy={energy}
						addressSearchSupportContent={addressSearchSupportContent}
						isNmiSearchModalOpen={isNmiModalOpen}
						onNmiSearchModalOpenChange={setIsNmiModalOpen}
						onClearClicked={() => {
							setValue("address", "");
							setValue("addressKey", "");
							setValue("nmi", "");
							setAddress("");
							setNmi("");
							clearErrors("address");
						}}
						onAddressSelected={(data: any) => {
							setValue("address", data.addressText);
							setValue("addressKey", data.globalAddressKey);
							setValue("nmi", "");
							setAddress(data.addressText);
							setNmi("");
							clearErrors("address");
						}}
						onInputChange={value => {
							setValue("address", value);
							setValue("addressKey", "");
							setValue("nmi", "");
							setAddress(value);
							setNmi("");
						}}
					/>

					<div className={styles["energy-type"]}>
						<RadioGroupField
							register={register}
							hideError
							className={styles["energy-type__field"]}
							innerClassName={styles["energy-type__field-inner"]}
							label={"Energy type"}
							name="energy"
							registerOptions={{
								required: "Please select an energy type.",
							}}
							options={[
								{
									label: "Electricity",
									value: "electricity",
								},
								{
									label: "Gas",
									value: "gas",
								},
							]}
						/>
					</div>
					<motion.div className={styles["promo"]}>
						<CheckboxInput
							className={styles["promo__checkbox"]}
							label="I have a promo code"
							{...register("hasPromoCode", {
								onChange: () => {
									resetField("promo");
								},
							})}
						/>
						<motion.div
							layout
							initial={{ height: 0, opacity: 0 }}
							animate={
								isPromoChecked
									? { height: "auto", opacity: 1, visibility: "visible" }
									: { height: 0, opacity: 0, visibility: "hidden" }
							}
							className={clsx(styles["promo__input-wrapper"])}
							aria-hidden={!isPromoChecked}
						>
							<span className={styles["promo__icon"]}>
								<MaterialIcon name="shoppingmode" />
							</span>
							<InputField
								register={register}
								inputProps={{ tabIndex: isPromoChecked ? 0 : -1 }}
								className={styles["promo__field"]}
								label="Promo code"
								placeholder="Enter code"
								name="promo"
								registerOptions={{
									required:
										isPromoChecked &&
										"Please enter a promo code or deselect the checkbox to continue",
									disabled: !isPromoChecked,
								}}
								hideError
								hideLabel
							/>
							{promoCode && (
								<button
									className={styles["promo__clear"]}
									aria-label="Clear promo code"
									onClick={() => resetField("promo", { defaultValue: "" })}
									type="button"
								>
									<MaterialIcon name="close" />
								</button>
							)}
						</motion.div>
					</motion.div>

					{Object.entries(errors).length > 0 && (
						<div className={styles["errors"]}>
							<span className={styles["errors__icon"]}>
								<MaterialIcon name="warning" />
							</span>
							<ul>
								{errors.address && (
									<li>
										<ErrorMessage
											errors={errors}
											name="address"
										/>
									</li>
								)}
								{errors.energy && (
									<li>
										<ErrorMessage
											errors={errors}
											name="energy"
										/>
									</li>
								)}
								{errors.promo && (
									<li>
										<ErrorMessage
											errors={errors}
											name="promo"
										/>{" "}
									</li>
								)}
							</ul>
						</div>
					)}
					<Button
						className={styles["product-lookup__submit"]}
						type="submit"
						showArrow
						disabled={formProcessing}
						label={formProcessing ? "Loading plans" : "See Plan"}
					/>
				</form>
			</FormProvider>
			{energy !== "gas" && (
				<NmiSearchModal
					isOpen={isNmiModalOpen}
					content={nmiSearchModalContent}
					onNmiSearch={handleNmiSearch}
					onOpenChange={setIsNmiModalOpen}
				/>
			)}
		</div>
	);
};

const getNmiDisplayValue = (nmi: string) => `NMI: ${nmi}`;
